/**
 * File Upload Service
 *
 * 处理文件上传到知识库的完整流程：
 * 1. 上传文件到 /api/v1/knowledge-bases/{kb_id}/files/upload (新API，一步完成上传和绑定)
 *
 * @deprecated 旧流程（已废弃）：
 * - /api/v1/files/upload - 上传文件到通用存储
 * - /api/v1/knowledge-bases/{kb_id}/files - 绑定文件到知识库
 * - 轮询上传进度和处理状态
 */

import http from '../lib/api';
import { fileAPI } from './api/fileApi';
import type {
  UploadingFile,
  UploadProgress,
  FrontendUploadStatus,
  KnowledgeBaseFile,
  UploadProgressResponse,
} from '../types/fileUpload';

/**
 * 从上传进度响应中提取上传进度信息
 *
 * 使用 total_bytes 和 bytes_uploaded 来判断上传进度和状态
 * 暂时忽略 status 字段
 *
 * @param response - 上传进度响应
 * @returns UploadProgress 对象
 */
function uploadProgress(response: UploadProgressResponse): UploadProgress {
  const { bytes_uploaded, total_bytes } = response;

  // 计算上传进度百分比
  const percentage = total_bytes > 0 ? Math.round((bytes_uploaded / total_bytes) * 100) : 0;

  // 确保百分比在 0-100 范围内
  const clampedPercentage = Math.max(0, Math.min(100, percentage));

  return {
    loaded: bytes_uploaded,
    total: total_bytes,
    percentage: clampedPercentage,
  };
}

class FileUploadService {
  // 批量上传文件队列
  private batchUploadQueues: Map<string, Map<string, UploadingFile>> = new Map();

  /**
   * 批量上传文件到知识库
   * 队列管理在服务层内部完成，组件层只需提供 onProgress 和 onComplete 回调
   *
   * @param files - 要上传的文件列表
   * @param knowledgeBaseId - 知识库ID
   * @param options - 回调函数（仅包含 onProgress 和 onComplete）
   */
  async uploadFilesToKnowledgeBase(
    files: File[],
    knowledgeBaseId: string,
    options?: {
      onProgress?: (progress: {
        current: number;
        total: number;
        fileName: string;
        fileSize: string;
        show: boolean;
      }) => void;
      onComplete?: (fileName: string, file: File) => void;
      onError?: (fileName: string, message: string) => void;
      t?: (key: string) => string;
    }
  ): Promise<void> {
    const t = options?.t || ((key: string) => key);
    const queueId = knowledgeBaseId;

    // 获取或创建队列
    if (!this.batchUploadQueues.has(queueId)) {
      this.batchUploadQueues.set(queueId, new Map());
    }
    const filesMap = this.batchUploadQueues.get(queueId)!;

    // 格式化文件大小（用于进度显示）
    const formatFileSize = (bytes: number): string => {
      if (bytes === 0) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    };

    // 计算并通知批量上传总进度
    const notifyBatchProgress = () => {
      if (!options?.onProgress) return;

      const filesArray = Array.from(filesMap.values());
      // 获取所有待上传和正在上传的文件（不包括已完成、失败或处理中的）
      const activeFiles = filesArray.filter(
        f => f.status === 'pending' || f.status === 'uploading'
      );
      const uploadingFiles = filesArray.filter(f => f.status === 'uploading');

      if (activeFiles.length > 0 || uploadingFiles.length > 0) {
        // 找到当前正在上传的文件，如果没有则找第一个待上传的文件
        const currentFile = uploadingFiles[0] || activeFiles.find(f => f.status === 'pending');

        if (currentFile) {
          // 计算当前文件的索引（在所有活跃文件中）
          const currentIndex = activeFiles.findIndex(f => f.id === currentFile.id) + 1;
          const total = activeFiles.length;

          options.onProgress({
            current: currentIndex,
            total: total,
            fileName: currentFile.file.name,
            fileSize: formatFileSize(currentFile.file.size),
            show: true,
          });
        }
      } else {
        // 没有正在上传的文件，隐藏进度
        options.onProgress({
          current: 0,
          total: 0,
          fileName: '',
          fileSize: '',
          show: false,
        });
      }
    };

    // 添加文件到队列
    files.forEach(file => {
      const uploadingFile: UploadingFile = {
        id: 'temp-' + Date.now() + '-' + Math.random().toString(36).substring(2, 11),
        file,
        status: 'pending',
        progress: { loaded: 0, total: file.size, percentage: 0 },
        message: t('knowledge.files.uploadStatus.pending'),
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      filesMap.set(uploadingFile.id, uploadingFile);
    });

    // 初始进度通知
    notifyBatchProgress();

    // 开始上传每个文件（并发上传）
    const uploadPromises = Array.from(filesMap.values()).map(async uploadingFile => {
      // 保存队列中的文件ID
      const queueFileId = uploadingFile.id;

      try {
        // 调用单个文件上传方法
        const result = await this.uploadFileToKnowledgeBase(uploadingFile.file, knowledgeBaseId, {
          t,
          onProgress: progress => {
            const file = filesMap.get(queueFileId);
            if (file) {
              file.progress = progress;
              file.updatedAt = new Date();
            }
            notifyBatchProgress();
          },
          onStatusChange: (status, message) => {
            const file = filesMap.get(queueFileId);
            if (file) {
              file.status = status;
              file.message = message;
              file.updatedAt = new Date();
            }
            notifyBatchProgress();

            // 当文件处理完成（embedding完成）时，3秒后从队列中移除
            if (status === 'completed') {
              setTimeout(() => {
                filesMap.delete(queueFileId);
                notifyBatchProgress();
              }, 3000);
            }
          },
          onUploadComplete: (fileName, _file) => {
            // 从队列中移除（上传完成，但可能还在处理embedding）
            filesMap.delete(queueFileId);
            // 通知单个文件上传完成，传递file对象
            options?.onComplete?.(fileName, uploadingFile.file);
            notifyBatchProgress();
          },
          onError: (fileName, message) => {
            // 从队列中移除（上传失败）
            filesMap.delete(queueFileId);
            // 通知单个文件上传失败
            options?.onError?.(fileName, message);
            notifyBatchProgress();
          },
        });

        // 上传完成后，更新队列中的文件对象，同步返回的 UploadingFile 的属性
        const file = filesMap.get(queueFileId);
        if (file && result) {
          file.fileId = result.fileId;
          file.kbFileId = result.kbFileId;
          file.uploadId = result.uploadId;
        }

        return result;
      } catch (error) {
        console.error(`Upload failed for file ${uploadingFile.file.name}:`, error);
        const file = filesMap.get(queueFileId);
        if (file) {
          file.status = 'error';
          file.updatedAt = new Date();
        }
        notifyBatchProgress();
        throw error;
      }
    });

    // 等待所有上传完成（不阻塞，继续执行）
    Promise.all(uploadPromises).catch(err => {
      console.error('Batch upload error:', err);
    });
  }

  /**
   * 获取批量上传队列
   *
   * @param knowledgeBaseId - 知识库ID
   * @returns 文件队列的 Map
   */
  getBatchUploadQueue(knowledgeBaseId: string): Map<string, UploadingFile> {
    return this.batchUploadQueues.get(knowledgeBaseId) || new Map();
  }

  /**
   * 清除批量上传队列
   *
   * @param knowledgeBaseId - 知识库ID
   */
  clearBatchUploadQueue(knowledgeBaseId: string): void {
    this.batchUploadQueues.delete(knowledgeBaseId);
  }

  /**
   * 上传文件到知识库（完整流程）
   *
   * @param file - 要上传的文件
   * @param knowledgeBaseId - 知识库ID
   * @param options - 回调函数
   * @returns UploadingFile 对象
   */
  async uploadFileToKnowledgeBase(
    file: File,
    knowledgeBaseId: string,
    options?: {
      onProgress?: (progress: UploadProgress) => void;
      onStatusChange?: (status: FrontendUploadStatus, message: string) => void;
      onUploadComplete?: (fileName: string, file: File) => void;
      onError?: (fileName: string, message: string) => void;
      t?: (key: string) => string; // i18n translation function
    }
  ): Promise<UploadingFile> {
    const t = options?.t || ((key: string) => key);

    // 创建上传文件对象
    const uploadingFile: UploadingFile = {
      id: 'temp-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9),
      file,
      status: 'pending',
      progress: { loaded: 0, total: file.size, percentage: 0 },
      message: t('knowledge.files.uploadStatus.pending'),
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    try {
      // ===== 使用新API: 直接上传到知识库 =====
      const uploadingMsg = t('knowledge.files.uploadStatus.uploading');
      options?.onStatusChange?.('uploading', uploadingMsg);
      uploadingFile.status = 'uploading';
      uploadingFile.message = uploadingMsg;

      // 使用新的API端点：/api/v1/knowledge-bases/{kb_id}/files/upload
      // 这个API会一步完成上传、绑定到知识库和启动处理流程
      const uploadResponse = await fileAPI.uploadFileToKnowledgeBase(
        knowledgeBaseId,
        file,
        progress => {
          uploadingFile.progress = progress;
          uploadingFile.updatedAt = new Date();
          options?.onProgress?.(progress);
        }
      );

      // 处理响应：POST /api/v1/knowledge-bases/{kb_id}/files/upload 返回格式：
      // KnowledgeBaseFile 对象
      const kbFileData = uploadResponse.data as any;
      uploadingFile.fileId = kbFileData.file_id || kbFileData.id;
      uploadingFile.kbFileId = kbFileData.id;

      // 上传完成，更新状态
      uploadingFile.progress.percentage = 100;
      uploadingFile.status = 'processing';
      const processingMsg = t('knowledge.files.uploadStatus.processing');
      uploadingFile.message = processingMsg;
      uploadingFile.updatedAt = new Date();
      options?.onStatusChange?.('processing', processingMsg);

      // 通知上层组件上传完成（文件已上传并绑定，正在处理中）
      options?.onUploadComplete?.(file.name, file);

      return uploadingFile;
    } catch (error: unknown) {
      console.error('Upload failed:', error);

      const errorMessage =
        (error as { response?: { data?: { detail?: string } }; message?: string })?.response?.data
          ?.detail ||
        (error as { message?: string })?.message ||
        t('knowledge.files.uploadStatus.uploadFailed');

      uploadingFile.status = 'error';
      uploadingFile.error = {
        code:
          (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
          'upload_failed',
        message: errorMessage,
      };
      const failedMsg = t('knowledge.files.uploadStatus.uploadFailed');
      uploadingFile.message = failedMsg;
      uploadingFile.updatedAt = new Date();

      options?.onStatusChange?.('error', failedMsg);
      // 通知上层组件上传失败
      options?.onError?.(file.name, errorMessage);
      throw error;
    }
  }

  /**
   * 轮询文件上传进度
   *
   * @deprecated 已废弃：新API不需要轮询上传进度，上传完成后直接返回
   *
   * 使用 /api/v1/files/upload/{upload_id}/progress 获取上传进度
   * 利用 uploadProgress 函数判断上传状态
   * 一旦上传完毕，通知上层组件关闭上传输入框，刷新页面
   * 然后在文件列表中的单个文件上轮询文件处理状态
   *
   * @param uploadingFile - 上传中的文件对象
   * @param knowledgeBaseId - 知识库ID
   * @param onProgress - 进度更新回调
   * @param onStatusChange - 状态变化回调
   * @param onUploadComplete - 上传完成回调（通知上层组件关闭上传框并刷新页面）
   */
  private pollUploadProgress(
    uploadingFile: UploadingFile,
    knowledgeBaseId: string,
    onProgress?: (progress: UploadProgress) => void,
    onStatusChange?: (status: FrontendUploadStatus, message: string) => void,
    onUploadComplete?: () => void
  ): void {
    if (!uploadingFile.uploadId) {
      console.warn('Cannot poll upload progress: missing upload ID');
      return;
    }

    const pollInterval = setInterval(async () => {
      try {
        const response = await http.get(
          '/api/v1/files/upload/' + uploadingFile.uploadId + '/progress'
        );

        const progressResponse = response.data as UploadProgressResponse;
        const progress = uploadProgress(progressResponse);

        // 更新上传进度
        uploadingFile.progress = progress;
        uploadingFile.updatedAt = new Date();
        onProgress?.(progress);

        // 判断上传是否完成：bytes_uploaded === total_bytes
        const isUploadComplete =
          progressResponse.bytes_uploaded >= progressResponse.total_bytes &&
          progressResponse.total_bytes > 0;

        if (isUploadComplete) {
          clearInterval(pollInterval);
          console.log('Upload completed:', uploadingFile.file.name);

          // 更新状态为processing，文件正在处理embedding
          uploadingFile.status = 'processing';
          uploadingFile.message = '处理中';
          uploadingFile.updatedAt = new Date();
          onStatusChange?.('processing', '处理中');

          // 通知上层组件上传完成（关闭上传框并刷新页面）
          // 文件会在文件列表中显示，通过文件列表页面的轮询来更新embedding状态
          onUploadComplete?.();
        }
      } catch (error: unknown) {
        console.error('Polling upload progress error:', error);
        const axiosError = error as { response?: { status?: number } };
        // 如果是404错误，可能上传已完成，停止轮询
        if (axiosError.response?.status === 404) {
          clearInterval(pollInterval);
          // 更新状态为processing，假设文件正在处理embedding
          uploadingFile.status = 'processing';
          uploadingFile.message = '处理中';
          uploadingFile.updatedAt = new Date();
          onStatusChange?.('processing', '处理中');
          // 通知上层组件，文件会在文件列表中显示，通过文件列表页面的轮询来更新状态
          onUploadComplete?.();
        }
      }
    }, 1000); // 每1秒轮询一次上传进度

    // 超时保护（10分钟后停止轮询）
    setTimeout(
      () => {
        clearInterval(pollInterval);
        console.warn('Upload progress polling timeout:', uploadingFile.file.name);
      },
      10 * 60 * 1000
    );
  }

  /**
   * 处理上传后的文件状态（当没有 upload_id 时的备用方案）
   *
   * @deprecated 已废弃：新API不需要此方法
   */
  private handleFileStatusAfterUpload(
    uploadingFile: UploadingFile,
    initialStatus: string,
    errorMessage: string | null | undefined,
    knowledgeBaseId: string,
    onStatusChange?: (status: FrontendUploadStatus, message: string) => void
  ): void {
    if (initialStatus === 'completed') {
      // 文件已经处理完成，直接更新状态
      uploadingFile.status = 'completed';
      uploadingFile.message = '上传成功';
      uploadingFile.updatedAt = new Date();
      onStatusChange?.('completed', '上传成功');
      console.log('File already completed:', uploadingFile.file.name);
    } else if (initialStatus === 'error') {
      // 文件处理出错
      uploadingFile.status = 'error';
      uploadingFile.error = {
        code: 'processing_failed',
        message: errorMessage || '处理失败',
      };
      uploadingFile.message = '处理失败';
      uploadingFile.updatedAt = new Date();
      onStatusChange?.('error', '处理失败');
      console.error('File processing failed:', uploadingFile.file.name);
    } else {
      // 文件正在处理中，开始轮询
      onStatusChange?.('processing', '正在处理');
      uploadingFile.status = 'processing';
      uploadingFile.message = '处理中';
      this.pollProcessingStatus(uploadingFile, knowledgeBaseId, onStatusChange);
    }
  }

  /**
   * 轮询文件 embedding 处理状态
   *
   * @deprecated 已废弃：文件列表页面会自己轮询文件状态，不需要在这里轮询
   *
   * 使用 /api/v1/files/{file_id}/progress 获取文件 embedding 处理状态
   * 此进度用于在文件卡片上显示
   *
   * @param uploadingFile - 上传中的文件对象
   * @param knowledgeBaseId - 知识库ID
   * @param onStatusChange - 状态变化回调
   * @param t - i18n translation function
   */
  private pollProcessingStatus(
    uploadingFile: UploadingFile,
    knowledgeBaseId: string,
    onStatusChange?: (status: FrontendUploadStatus, message: string) => void,
    t?: (key: string) => string
  ): void {
    const translate = t || ((key: string) => key);
    if (!uploadingFile.fileId) {
      console.warn('Cannot poll status: missing file ID');
      return;
    }

    const pollInterval = setInterval(async () => {
      try {
        // 使用 /api/v1/files/{file_id}/progress 获取文件 embedding 处理状态
        const response = await http.get('/api/v1/files/' + uploadingFile.fileId + '/progress');

        // 兼容两种响应格式：直接返回文件对象，或包装在 file 字段中
        const fileData = (response.data as any)?.file || response.data;
        const status = fileData.status;
        console.log(`Polling file embedding status: ${uploadingFile.file.name}, status: ${status}`);

        if (status === 'completed') {
          const msg = translate('knowledge.files.uploadStatus.uploadCompleted');
          uploadingFile.status = 'completed';
          uploadingFile.message = msg;
          uploadingFile.updatedAt = new Date();
          onStatusChange?.('completed', msg);
          clearInterval(pollInterval);
          console.log('File embedding completed:', uploadingFile.file.name);
        } else if (status === 'error') {
          const msg = translate('knowledge.files.uploadStatus.processingFailed');
          uploadingFile.status = 'error';
          uploadingFile.error = {
            code: 'processing_failed',
            message: fileData.error_message || msg,
          };
          uploadingFile.message = msg;
          uploadingFile.updatedAt = new Date();
          onStatusChange?.('error', msg);
          clearInterval(pollInterval);
          console.error('File embedding failed:', uploadingFile.file.name);
        } else if (status === 'processing') {
          // 继续轮询，文件正在 embedding 处理中
        } else {
          // 未知状态，记录日志但继续轮询
          console.warn(`Unknown file status: ${status} for file: ${uploadingFile.file.name}`);
        }
      } catch (error: unknown) {
        console.error('Polling error:', error);
        // 如果是404错误，可能是文件不存在，停止轮询
        const axiosError = error as { response?: { status?: number } };
        if (axiosError.response?.status === 404) {
          const msg = translate('knowledge.files.uploadStatus.fileNotFound');
          uploadingFile.status = 'error';
          uploadingFile.error = {
            code: 'file_not_found',
            message: msg,
          };
          uploadingFile.message = msg;
          uploadingFile.updatedAt = new Date();
          onStatusChange?.('error', msg);
          clearInterval(pollInterval);
        }
      }
    }, 2000); // 每2秒轮询一次

    // 超时保护（5分钟后停止轮询）
    setTimeout(
      () => {
        clearInterval(pollInterval);
        if (uploadingFile.status === 'processing') {
          const msg = translate('knowledge.files.uploadStatus.processingTimeout');
          uploadingFile.status = 'error';
          uploadingFile.message = msg;
          uploadingFile.updatedAt = new Date();
          onStatusChange?.('error', msg);
          console.warn('Polling timeout for file:', uploadingFile.file.name);
        }
      },
      5 * 60 * 1000
    );
  }

  /**
   * 获取知识库文件列表
   *
   * @param knowledgeBaseId - 知识库ID
   * @param params - 查询参数
   * @returns 文件列表
   */
  async getKnowledgeBaseFiles(
    knowledgeBaseId: string,
    params?: {
      page?: number;
      page_size?: number;
      status?: 'pending' | 'processing' | 'completed' | 'error';
      search?: string;
    }
  ): Promise<{ items: KnowledgeBaseFile[]; total: number }> {
    try {
      const response = await http.get('/api/v1/knowledge-bases/' + knowledgeBaseId + '/files', {
        params,
      });

      // 处理不同的响应格式
      if (Array.isArray(response.data)) {
        return {
          items: response.data,
          total: (response.data as any).length,
        };
      } else if ((response.data as any)?.items && Array.isArray((response.data as any).items)) {
        return {
          items: (response.data as any).items,
          total: (response.data as any).total || (response.data as any).items.length,
        };
      }

      return { items: [], total: 0 };
    } catch (error) {
      console.error('Failed to fetch files:', error);
      return { items: [], total: 0 };
    }
  }

  /**
   * 重新处理失败的文件
   *
   * @param knowledgeBaseId - 知识库ID
   * @param fileId - 文件ID
   * @returns 是否成功
   */
  async reprocessFile(knowledgeBaseId: string, fileId: string): Promise<boolean> {
    try {
      await http.post(
        '/api/v1/knowledge-bases/' + knowledgeBaseId + '/files/' + fileId + '/reprocess'
      );
      return true;
    } catch (error) {
      console.error('Failed to reprocess file:', error);
      return false;
    }
  }

  /**
   * 删除知识库中的文件
   *
   * @param knowledgeBaseId - 知识库ID
   * @param fileId - 文件ID
   * @returns 是否成功
   */
  async deleteFile(knowledgeBaseId: string, fileId: string): Promise<boolean> {
    try {
      await http.delete('/api/v1/knowledge-bases/' + knowledgeBaseId + '/files/' + fileId);
      return true;
    } catch (error) {
      console.error('Failed to delete file:', error);
      return false;
    }
  }

  /**
   * 下载知识库中的文件
   *
   * @param knowledgeBaseId - 知识库ID
   * @param fileId - 文件ID
   * @param fileName - 文件名（可选，如果未提供则从响应头或使用默认名称）
   * @returns 是否下载成功
   */
  async downloadFile(knowledgeBaseId: string, fileId: string, fileName?: string): Promise<boolean> {
    try {
      const response = await http.get(
        '/api/v1/knowledge-bases/' + knowledgeBaseId + '/files/' + fileId + '/download',
        { responseType: 'blob' }
      );

      // 从响应头获取文件名，如果响应头中没有则使用传入的文件名
      let downloadFileName = fileName;
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (fileNameMatch && fileNameMatch[1]) {
          downloadFileName = fileNameMatch[1].replace(/['"]/g, '');
          // 处理URL编码的文件名
          try {
            downloadFileName = decodeURIComponent(downloadFileName);
          } catch (e) {
            // 如果解码失败，使用原始值
          }
        }
      }

      // 如果还是没有文件名，使用默认名称
      if (!downloadFileName) {
        downloadFileName = `file-${fileId}`;
      }

      // 创建Blob对象
      const blob = new Blob([response.data as BlobPart]);

      // 创建临时URL并触发下载
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadFileName;
      document.body.appendChild(link);
      link.click();

      // 清理
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      return true;
    } catch (error) {
      console.error('Failed to download file:', error);
      return false;
    }
  }
}

export const fileUploadService = new FileUploadService();
export default fileUploadService;
